/**
 * Supabase Authentication Service
 * 
 * This module provides authentication functionality using Supabase.
 * It includes methods for user authentication, registration, and management.
 */

import { supabase } from './client'
import { User } from '@supabase/supabase-js'
import { Platform } from '../../utils/platform'

// Response type for authentication operations
export interface AuthError {
  message: string
  code?: string
}

export interface AuthResponse<T = User> {
  data: T | null
  error: AuthError | null
}

// Export the auth service methods
export const signInWithEmailAndPassword = async (email: string, password: string): Promise<AuthResponse> => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (error) {
      return {
        data: null,
        error: {
          message: error.message,
          code: error.code
        }
      }
    }

    return {
      data: data.user,
      error: null
    }
  } catch (error: any) {
    return {
      data: null,
      error: {
        message: error.message || 'Failed to sign in',
        code: error.code
      }
    }
  }
}

export const signInWithGoogle = async (): Promise<AuthResponse> => {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    })

    if (error) {
      return {
        data: null,
        error: {
          message: error.message,
          code: error.code
        }
      }
    }

    // For OAuth, we don't get the user immediately since it redirects
    return {
      data: null,
      error: null
    }
  } catch (error: any) {
    return {
      data: null,
      error: {
        message: error.message || 'Failed to sign in with Google',
        code: error.code
      }
    }
  }
}

export const signInWithApple = async (): Promise<AuthResponse> => {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: window.location.origin
      }
    })

    if (error) {
      return {
        data: null,
        error: {
          message: error.message,
          code: error.code
        }
      }
    }

    // For OAuth, we don't get the user immediately since it redirects
    return {
      data: null,
      error: null
    }
  } catch (error: any) {
    return {
      data: null,
      error: {
        message: error.message || 'Failed to sign in with Apple',
        code: error.code
      }
    }
  }
}

export const signOut = async (): Promise<AuthResponse<null>> => {
  try {
    const { error } = await supabase.auth.signOut()

    if (error) {
      return {
        data: null,
        error: {
          message: error.message,
          code: error.code
        }
      }
    }

    return {
      data: null,
      error: null
    }
  } catch (error: any) {
    return {
      data: null,
      error: {
        message: error.message || 'Failed to sign out',
        code: error.code
      }
    }
  }
}

export const getCurrentUser = async (): Promise<AuthResponse> => {
  try {
    const { data, error } = await supabase.auth.getUser()

    if (error) {
      return {
        data: null,
        error: {
          message: error.message,
          code: error.code
        }
      }
    }

    return {
      data: data.user,
      error: null
    }
  } catch (error: any) {
    return {
      data: null,
      error: {
        message: error.message || 'Failed to get current user',
        code: error.code
      }
    }
  }
}

export const resetPassword = async (email: string): Promise<AuthResponse<null>> => {
  try {
    let options = {};
    
    // Only add redirectTo for web platforms, not for React Native
    if (typeof window !== 'undefined' && !Platform.isNative) {
      options = {
        redirectTo: `${window.location.origin}/reset-password`
      };
    }
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, options);

    if (error) {
      return {
        data: null,
        error: {
          message: error.message,
          code: error.code
        }
      }
    }

    return {
      data: null,
      error: null
    }
  } catch (error: any) {
    console.error('Reset password error:', error);
    return {
      data: null,
      error: {
        message: error.message || 'Failed to send password reset email',
        code: error.code
      }
    }
  }
}

export const updatePassword = async (newPassword: string): Promise<AuthResponse> => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    })

    if (error) {
      return {
        data: null,
        error: {
          message: error.message,
          code: error.code
        }
      }
    }

    return {
      data: data.user,
      error: null
    }
  } catch (error: any) {
    return {
      data: null,
      error: {
        message: error.message || 'Failed to update password',
        code: error.code
      }
    }
  }
}

export const updateProfile = async (profile: { displayName?: string, photoURL?: string }): Promise<AuthResponse> => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      data: {
        display_name: profile.displayName,
        avatar_url: profile.photoURL
      }
    })

    if (error) {
      return {
        data: null,
        error: {
          message: error.message,
          code: error.code
        }
      }
    }

    return {
      data: data.user,
      error: null
    }
  } catch (error: any) {
    return {
      data: null,
      error: {
        message: error.message || 'Failed to update profile',
        code: error.code
      }
    }
  }
}
